import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Lock, Check } from 'lucide-react';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { NavBar } from '../components/NavBar';
import { ClubBuilderBasicsPanel } from '../components/ClubBuilderBasicsPanel';
import { PackSizesPanel } from '../components/PackSizesPanel';
import type { PackSize } from '../components/PackSizesPanel';
import { initialPackSizes } from '../data/mockReleasePack';
import { MembershipPanel } from '../components/MembershipPanel';
import { FlowPanel } from '../components/FlowPanel';
import { ThemingPanel } from '../components/ThemingPanel';
import { SellingPlansPanel } from '../components/SellingPlansPanel';
const TABS = [{
  id: 'tab-basics',
  label: 'Basics'
}, {
  id: 'tab-pack-sizes',
  label: 'Pack Sizes',
  count: 0
}, {
  id: 'tab-membership',
  label: 'Membership'
}, {
  id: 'tab-flow',
  label: 'Flow'
}, {
  id: 'tab-theming',
  label: 'Theming'
}, {
  id: 'tab-selling-plans',
  label: 'Selling Plans',
  count: 0
}] as const;
// Tab index constants
const TAB_INDEX: Record<string, number> = {
  'tab-basics': 0,
  'tab-pack-sizes': 1,
  'tab-membership': 2,
  'tab-flow': 3,
  'tab-theming': 4,
  'tab-selling-plans': 5
};
export function BoxBuilderPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('tab-basics');
  const [completedUpTo, setCompletedUpTo] = useState<number>(-1);
  const [boxType, setBoxType] = useState<'custom' | 'curated'>('custom');
  const [packSizes, setPackSizes] = useState<PackSize[]>(initialPackSizes);
  const [toast, setToast] = useState<string | null>(null);
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };
  // Central advance function: marks tab at `index` as completed and moves to next tab
  const advanceFrom = (index: number) => {
    setCompletedUpTo(prev => Math.max(prev, index));
    const nextTab = TABS[index + 1];
    if (nextTab) {
      setActiveTab(nextTab.id);
    }
  };
  const previewPublishEnabled = completedUpTo >= 4;
  const tabsWithCounts = TABS.map(tab => {
    if (tab.id === 'tab-pack-sizes') return {
      ...tab,
      count: packSizes.length
    };
    return tab;
  });
  return <div className="bg-[#F1F5F9] min-h-screen p-6" data-id="element-7459" data-mp="3sa5piodnayfq">
      {/* Standard NavBar */}
      <div className="mb-6" data-id="element-7460" data-mp="jsrpdeyjfofl">
        <NavBar tabs={[]} title="Club Builder" data-id="element-7461" data-mp="jum2w87hce9n" />
      </div>

      {/* Page Header */}
      <div className="mb-6" data-id="element-7462" data-mp="otfchpjnbl4j">
        <button onClick={() => navigate('/club-builder')} className="flex items-center gap-1.5 text-[14px] text-[#475569] hover:text-[#0F172A] transition-colors mb-4" data-id="element-7463" data-mp="a2py6eaw1pwg">
          <ArrowLeft className="w-4 h-4" data-id="element-7464" data-mp="ezgen2qlunie" />
          Back to Club Builder
        </button>

        <div className="flex items-center justify-between" data-id="element-7465" data-mp="12pst6wgcvihy">
          <div className="flex items-center gap-3" data-id="element-7466" data-mp="17ivsll7tdm62">
            <h2 className="text-[28px] font-bold text-[#0F172A]" data-id="element-7467" data-mp="17jot44g6jffr">
              New Wine Club
            </h2>
            <Badge variant="warning" data-id="element-7468" data-mp="2n964f0awth15">Draft</Badge>
          </div>

          <div className="flex items-center gap-3" data-id="element-7469" data-mp="2n8wetdts1bvz">
            <Button variant="outline" iconLeft={<Eye className="w-4 h-4" data-id="element-7471" data-mp="31xj52akxzo1z" />} size="sm" disabled={!previewPublishEnabled} data-id="element-7470" data-mp="31ykiglpfkmmv">
              Preview
            </Button>
            <Button variant="outline" size="sm" onClick={() => showToast('Draft saved')} data-id="element-7472" data-mp="2x1bwsb52q0gv">
              Save Draft
            </Button>
            <Button variant="primary" size="sm" disabled={!previewPublishEnabled} onClick={() => showToast('Wine club published!')} data-id="element-7473" data-mp="24b3ln38yggbs">
              Publish
            </Button>
          </div>
        </div>
      </div>

      {toast && <div className="fixed bottom-6 right-6 z-50 bg-[#10B981] text-white text-sm font-medium px-4 py-3 rounded-lg shadow-lg" data-id="element-7474" data-mp="2e7oopj1ezhcz">
          {toast}
        </div>}

      {/* Tab Navigation */}
      <nav className="bg-white border-b border-[#E2E8F0] rounded-t-lg px-6 flex items-end" data-id="element-7475" data-mp="3pmllxg7r5cvf">
        {tabsWithCounts.map(tab => {
        const tabIndex = TAB_INDEX[tab.id];
        const isActive = activeTab === tab.id;
        const isLocked = tabIndex > completedUpTo && !isActive;
        const isCompleted = tabIndex <= completedUpTo && !isActive;
        return <button key={tab.id} onClick={() => {
          if (!isLocked) setActiveTab(tab.id);
        }} disabled={isLocked} className={`
                relative flex items-center gap-1.5
                text-[13px] font-semibold
                px-[18px] py-3
                border-b-2 transition-colors duration-200
                focus:outline-none
                ${isActive ? 'text-[#0EA5E9] border-[#0EA5E9]' : isCompleted ? 'text-[#10B981] border-transparent hover:text-[#059669]' : 'text-[#94A3B8] border-transparent cursor-not-allowed'}
              `} data-id="element-7476" data-mp="12rx7xhcibi3c">
              {isCompleted && <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#10B981] flex-shrink-0" data-id="element-7477" data-mp="1qfye4jm7qmfl">
                  <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-7478" data-mp="3fzg1y2ek0vjq" />
                </span>}
              {isLocked && <Lock className="w-3.5 h-3.5 flex-shrink-0" data-id="element-7479" data-mp="3azk6wsnu4egb" />}
              {tab.label}
              {'count' in tab && tab.count !== undefined && <span className={`
                    inline-flex items-center justify-center
                    text-[11px] font-semibold
                    rounded-full min-w-[20px] h-5 px-1.5
                    ${isActive ? 'bg-[#E0F2FE] text-[#0284C7]' : isCompleted ? 'bg-[#D1FAE5] text-[#059669]' : 'bg-[#F1F5F9] text-[#94A3B8]'}
                  `} data-id="element-7480" data-mp="360z7uwkbk0tg">
                  {tab.count}
                </span>}
            </button>;
      })}
      </nav>

      {/* Tab Content */}
      <div className="pt-6" data-id="element-7481" data-mp="3jzqiua3vx6dd">
        {activeTab === 'tab-basics' && <ClubBuilderBasicsPanel onNext={() => advanceFrom(0)} boxType={boxType} onBoxTypeChange={setBoxType} data-id="element-7482" data-mp="la86nix5g63q" />}

        {activeTab === 'tab-pack-sizes' && <PackSizesPanel boxType={boxType} onBack={() => setActiveTab('tab-basics')} onNext={() => {
        if (packSizes.length > 0) advanceFrom(1);
      }} packSizes={packSizes} setPackSizes={setPackSizes} data-id="element-7483" data-mp="gi8c65zum5ej" />}

        {activeTab === 'tab-membership' && <MembershipPanel onBack={() => setActiveTab('tab-pack-sizes')} onNext={() => advanceFrom(2)} packSizes={packSizes} data-id="element-7484" data-mp="2123p3lzu3ad1" />}

        {activeTab === 'tab-flow' && <FlowPanel onBack={() => setActiveTab('tab-membership')} onNext={() => advanceFrom(3)} data-id="element-7485" data-mp="25zc9lt8gfqhg" />}

        {activeTab === 'tab-theming' && <ThemingPanel onBack={() => setActiveTab('tab-flow')} onNext={() => advanceFrom(4)} data-id="element-7486" data-mp="1s3i6xry9xxcs" />}

        {activeTab === 'tab-selling-plans' && <SellingPlansPanel onBack={() => setActiveTab('tab-theming')} data-id="element-7487" data-mp="1i3rgc1cu42hz" />}
      </div>
    </div>;
}